import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faServer, faCheckCircle, faExclamationTriangle, faTimesCircle,
  faDatabase, faClipboardList, faUsers, faFileAlt, faClock, faBell
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

interface SystemHealthWidgetProps {
  health?: {
    database?: string;
    submissions?: number;
    suppliers?: number;
    templates?: number;
    overdueCount?: number;
    remindersDue?: number;
    issues?: string[];
    lastCheck?: string;
  };
  isRunning?: boolean;
}

const SystemHealthWidget: React.FC<SystemHealthWidgetProps> = ({ 
  health, 
  isRunning = false 
}) => {
  // **SAFE DEFAULTS**
  const safeHealth = {
    database: health?.database || 'UNKNOWN',
    submissions: health?.submissions || 0,
    suppliers: health?.suppliers || 0,
    templates: health?.templates || 0,
    overdueCount: health?.overdueCount || 0,
    remindersDue: health?.remindersDue || 0,
    issues: health?.issues || [],
    lastCheck: health?.lastCheck || new Date().toISOString()
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'OK': return faCheckCircle;
      case 'WARNING': return faExclamationTriangle;
      case 'ERROR': return faTimesCircle;
      default: return faServer;
    }
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'OK': return 'text-green-500';
      case 'WARNING': return 'text-yellow-500';
      case 'ERROR': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getOverallHealth = () => {
    if (!health) return 'UNKNOWN';
    if (safeHealth.issues.length > 0) return 'WARNING';
    if (safeHealth.database !== 'OK') return 'ERROR';
    return 'OK';
  };

  const overallHealth = getOverallHealth();

  const formatLastCheck = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString('de-DE');
    } catch {
      return 'Unbekannt';
    }
  };

  // **LOADING STATE**
  if (!health) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faServer} className="text-2xl text-gray-400 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              <p className="text-sm text-gray-600">Lade Systemstatus...</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="animate-pulse bg-gray-200 h-8 w-8 mx-auto mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-4 w-12 mx-auto mb-1 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-3 w-8 mx-auto rounded"></div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-lg shadow-sm p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faServer} className="text-2xl text-purple-500 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
            <p className="text-sm text-gray-600">
              Letzter Check: {formatLastCheck(safeHealth.lastCheck)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className={`flex items-center px-3 py-1 rounded-full ${
            overallHealth === 'OK' ? 'bg-green-100 text-green-700' :
            overallHealth === 'WARNING' ? 'bg-yellow-100 text-yellow-700' :
            overallHealth === 'ERROR' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            <FontAwesomeIcon icon={getHealthIcon(overallHealth)} className="mr-2" />
            <span className="text-sm font-medium">{overallHealth}</span>
          </div>
          
          <div className={`px-2 py-1 rounded text-xs ${
            isRunning ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {isRunning ? 'Aktiv' : 'Inaktiv'}
          </div>
        </div>
      </div>

      {/* **SYSTEM METRICS** */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <FontAwesomeIcon icon={faDatabase} className="text-2xl text-blue-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Database</p>
          <p className={`text-xs font-medium ${getHealthColor(safeHealth.database)}`}>
            {safeHealth.database}
          </p>
        </div>
        
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <FontAwesomeIcon icon={faClipboardList} className="text-2xl text-green-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Submissions</p>
          <p className="text-xs text-gray-600">{safeHealth.submissions.toLocaleString()}</p>
        </div>
        
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <FontAwesomeIcon icon={faUsers} className="text-2xl text-purple-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Suppliers</p>
          <p className="text-xs text-gray-600">{safeHealth.suppliers.toLocaleString()}</p>
        </div>
        
        <div className="text-center p-4 bg-indigo-50 rounded-lg">
          <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-indigo-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Templates</p>
          <p className="text-xs text-gray-600">{safeHealth.templates.toLocaleString()}</p>
        </div>
        
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <FontAwesomeIcon icon={faClock} className="text-2xl text-red-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Überfällig</p>
          <p className="text-xs text-gray-600">{safeHealth.overdueCount.toLocaleString()}</p>
        </div>
        
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <FontAwesomeIcon icon={faBell} className="text-2xl text-orange-500 mb-2" />
          <p className="text-sm font-medium text-gray-900">Erinnerungen</p>
          <p className="text-xs text-gray-600">{safeHealth.remindersDue.toLocaleString()}</p>
        </div>
      </div>

      {/* **ISSUES** */}
      {safeHealth.issues.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 mr-2" />
            <span className="font-medium text-yellow-800">
              {safeHealth.issues.length} Warnung{safeHealth.issues.length !== 1 ? 'en' : ''}
            </span>
          </div>
          <ul className="list-disc list-inside space-y-1">
            {safeHealth.issues.map((issue, index) => (
              <li key={index} className="text-yellow-700 text-sm">{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {/* **HEALTHY STATE** */}
      {safeHealth.issues.length === 0 && overallHealth === 'OK' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-3xl mb-2" />
          <p className="text-green-800 font-medium">Alle Systeme laufen normal</p>
          <p className="text-green-700 text-sm">Keine bekannten Probleme gefunden</p>
        </div>
      )}
    </motion.div>
  );
};

export default SystemHealthWidget;